// Live collaboration for the GLOBAL chat. Reuses the designer document channel
// as a generic relay (it broadcasts `chat`/`presence` to a tenant+room without
// validating the room is a real doc): one room keyed 'global_chat', with
// messages multiplexed by `session_id` so a peer's message only renders into the
// matching, *shared* session. Singleton so the /chat page and the dock share a
// single socket. Connects lazily the first time the global chat is shown.
//
// Mirrors DesignerChat's sendLive/receiveLive pattern, but the receiver wires
// straight into the chat store (registered once here, not per-surface, so a peer
// message isn't appended twice). The gateway relays to peers EXCEPT the sender,
// so there's no echo of your own messages.
import { reactive, ref } from 'vue';
import {
  connectDocChannel,
  type ChatMessage, type ChatSend, type DocChannel,
} from '../designer/docChannel';
import { callTool } from '../api/client';
import { useChatStore, type ChatAttachment } from '../stores/chat';
import { useAuthStore } from '../stores/auth';

const ROOM = 'global_chat';

export interface ChatPeer { id: string; by?: string; color: string; label?: string }

const peers = reactive<Record<string, ChatPeer>>({});
// Display name of a collaborator currently generating in the *active* session.
const typingBy = ref<string | null>(null);
let typingTimer: ReturnType<typeof setTimeout> | null = null;

let channel: DocChannel | null = null;
let connecting = false;
let heartbeat: ReturnType<typeof setInterval> | null = null;

function liveImagesToAttachments(imgs: ChatMessage['images']): ChatAttachment[] {
  return (imgs ?? []).map((i) => ({
    mediaType: i.mediaType as ChatAttachment['mediaType'],
    dataBase64: i.dataBase64,
    dataUrl: `data:${i.mediaType};base64,${i.dataBase64}`,
  }));
}

function onPeerChat(m: ChatMessage): void {
  const store = useChatStore();
  // Only render into the session it targets, and only while that session is the
  // one on screen and shared (matches the sender's gating).
  if (!m.session_id || m.session_id !== store.activeId) return;
  if (store.activeSession?.visibility !== 'shared') return;

  if (m.kind === 'typing') {
    typingBy.value = peers[m.from]?.label ?? m.by ?? 'A collaborator';
    if (typingTimer) clearTimeout(typingTimer);
    typingTimer = setTimeout(() => { typingBy.value = null; }, 8000);
    return;
  }
  // A full message (peer's user turn, or the assistant turn a peer drove).
  typingBy.value = null;
  if (typingTimer) clearTimeout(typingTimer);
  store.pushLocal({
    role: m.role,
    content: m.text ?? '',
    ...(m.images?.length ? { images: liveImagesToAttachments(m.images) } : {}),
  });
}

async function ensureConnected(): Promise<void> {
  if (channel || connecting) return;
  connecting = true;
  try {
    const r = await callTool<{ token?: string }>('designer_live_token', { doc_id: ROOM });
    if (!r?.token) return;
    channel = connectDocChannel({
      token: r.token,
      docId: ROOM,
      onOps: () => { /* chat room carries no document ops */ },
      onPresence: (m) => { peers[m.from] = { id: m.from, by: m.by, color: m.color, label: m.label }; },
      onPresenceLeave: (m) => { delete peers[m.from]; },
      onChat: onPeerChat,
    });
    announcePresence();
    // Re-announce periodically so peers who join later still see us (presence
    // isn't replayed on join). presence_leave cleans us up on disconnect.
    if (!heartbeat) heartbeat = setInterval(announcePresence, 12000);
  } finally {
    connecting = false;
  }
}

function announcePresence(): void {
  if (!channel) return;
  const auth = useAuthStore();
  channel.sendPresence({ label: auth.userDisplayName ?? auth.manualUserSub ?? 'Someone' });
}

export function useChatLive() {
  /** Open the live room (idempotent). Call when the global chat is shown. */
  function connect(): void { void ensureConnected(); }
  /** Broadcast a live message to the room (lazily connects first). */
  function broadcast(m: ChatSend): void { void ensureConnected().then(() => channel?.sendChat(m)); }
  return { peers, typingBy, connect, broadcast, announcePresence };
}
